import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { usePost } from "../../hooks/usePost";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../store/ToastProvider";
import { NavBar } from "../../components/common/NavBar";
import { SideMenu } from "../../components/common/SideMenu";
import { CompanyTabBar } from "./CompanyTabBar";
import { CompanyStatusHeader } from "./CompanyStatusheader";
import { greyText } from "../../styles";
import {
  BackgroundContainer,
  MainColumn as Column,
  NavBarContainer,
  RowContainer,
} from "../../components/containers/styledComponents";

import { getPathwayFromInput } from "../../components/visuals/PathwayProgressBar";
import {
  CompassMainHeader,
  CompassParagraph,
} from "../../components/common/styledComponents";
import { formatReadable } from "../../utils/formattingUtils";
import { MESSAGES } from "../../messages";
import { CompanyExposure } from "./Exposure/CompanyExposure";
import { VotingRecords } from "../../components/common/Voting/VotingRecords";
import { CompanyEngagement } from "./Engagement/CompanyEngagement";
import { InlineLoadingPlaceholder } from "../../components/visuals/InlineLoadingPlaceholder";
import {
  CompanySummary,
  CompanySummaryRequest,
} from "../../models/CompanyprofileModels";
import { ROUTES } from "../../routes/routeNames";

export const CompanyProfile = (): JSX.Element => {
  const [show, setShow] = useState<boolean>(false);
  const [companyName, setCompanyName] = useState<string>("");
  const { companyId } = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const {
    data: companySummaryData,
    sendData,
    isLoading,
    error: summaryDataError,
  } = usePost<CompanySummary, CompanySummaryRequest>();

  const pathway = getPathwayFromInput(
    companySummaryData?.implicit_temperature || MESSAGES.NOT_AVAILABLE,
  );

  const votingAlignmentFilters = useMemo(() => {
    return { compass_cid: [companyId as string] };
  }, [companyId]);

  useEffect(() => {
    sendData("GET_COMPANY_SUMMARY", {
      compass_cid: companyId as string,
      company_name: companyName,
    });
  }, [companyId]);

  useEffect(() => {
    if (companySummaryData) {
      setCompanyName(formatReadable(companySummaryData.name));
    }
  }, [companySummaryData]);

  useEffect(() => {
    if (summaryDataError) {
      addToast(MESSAGES.SCREENING_TOOL.ERROR_FETCHING_COMPANY_SUMMARY, "error");
      navigate(ROUTES.SCREENING_TOOL);
    }
  }, [summaryDataError]);

  return (
    <BackgroundContainer>
      <Column flex={show ? "0 0 15%" : "0 0 0%"} show={show}>
        <SideMenu show={show} setShow={setShow} />
      </Column>
      <Column flex="1" bg="#F5F5F5" overflow={true}>
        <NavBarContainer>
          <RowContainer mt={1} mb={1}>
            <NavBar
              address={
                companyName ? (
                  `company-profile / ${companyName}`
                ) : (
                  <>
                    company-profile /{" "}
                    <InlineLoadingPlaceholder label="Loading company name" />
                  </>
                )
              }
            />
          </RowContainer>
          <RowContainer ai={"flex-start"} jc={"flex-start"} mt={-15}>
            <CompassMainHeader pt={1} ml={12}>
              Company Profile
            </CompassMainHeader>
          </RowContainer>
          <RowContainer ai={"flex-start"} jc={"flex-start"} mt={1}>
            <CompassParagraph ml={20} color={greyText}>
              {MESSAGES.SCREENING_TOOL.COMPANY_PROFILE}
            </CompassParagraph>
          </RowContainer>
          <RowContainer ml={20} mt={1} ai="center">
            <CompanyStatusHeader
              companyId={companyId}
              companyName={companyName}
              pathway={pathway}
              data={companySummaryData}
              isLoading={isLoading}
              showNews={true}
            />
          </RowContainer>
        </NavBarContainer>
        <CompanyTabBar data={companySummaryData} />
        <CompanyExposure companyId={companyId as string} />
        <VotingRecords
          filters={votingAlignmentFilters} // as stewardship filter params
          companySummaryData={companySummaryData}
        />
        <CompanyEngagement companyId={companyId as string} />
      </Column>
    </BackgroundContainer>
  );
};
